import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  XStack,
  YStack,
  useTheme,
  Button,
  Stack,
  Card,
  Dialog
} from 'tamagui';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ReactIcon from '../../../assets/icons/React';

import { CobuText } from '@cobu/ui';
import { useReactions, ReactionType, DEFAULT_REACTIONS } from '../../../hooks/useReactions';
import { defaultEmojis } from '../../../features/reactions/emoji';

const DEFAULT_EMOJI_TITLES = new Set(defaultEmojis.map(e => e.title.toLowerCase()));
import { GroupComment } from '@cobu/types';
import { Strings } from '../../../constants';
import { useEmojiPalette } from '../../../features/reactions/context/EmojiProvider';

interface EnhancedReactionSelectorProps {
  comment: GroupComment;
  currentUserId: string;
  isParent?: boolean;
  onReactToComment?: (commentId: string, reaction: string | null) => Promise<any>;
  onShowReactionModal?: (commentId: string) => void;
  disabled?: boolean;
}

const EnhancedReactionSelector: React.FC<EnhancedReactionSelectorProps> = memo(({
  comment,
  currentUserId,
  isParent = true,
  onReactToComment,
  onShowReactionModal,
  disabled = false,
}) => {
  const theme = useTheme();
  const strings = Strings.get().discussion;

  const [showFullPicker, setShowFullPicker] = useState(false);
  const [pickerVisible, setPickerVisible] = useState(false);

  // swallow the onPress that follows a long-press
  const longPressingRef = useRef(false);

  // Use the reactions hook
  const reactions = useReactions({
    comment,
    currentUserId,
    onReactToComment,
    onShowReactionModal,
  });

  // Emoji palette (custom + defaults)
  const { emojis, byTitle } = useEmojiPalette();

  // Constants
  const MAX_VISIBLE_REACTIONS = 4;

  // Order palette: keep current reaction first if present
  const orderedEmojiTitles = useMemo(() => {
    const titles = (emojis || []).map(e => e.title);
    if (reactions.currentReaction) {
      const idx = titles.findIndex(t => t === reactions.currentReaction);
      if (idx > -1) {
        const copy = [...titles];
        const [cur] = copy.splice(idx, 1);
        copy.unshift(cur);
        return copy;
      }
    }
    return titles;
  }, [emojis, reactions.currentReaction]);

  // Visible and hidden lists
  const visibleTitles = useMemo(() => orderedEmojiTitles.slice(0, MAX_VISIBLE_REACTIONS), [orderedEmojiTitles]);
  const hiddenTitles = useMemo(() => orderedEmojiTitles.slice(MAX_VISIBLE_REACTIONS), [orderedEmojiTitles]);

  /**
   * Handle reaction selection
   */
  const handleReactionPress = useCallback(async (reactionType: ReactionType) => {
    if (disabled) return;

    if (reactions.currentReaction === reactionType) {
      await reactions.removeReaction();
    } else {
      await reactions.addReaction(reactionType);
    }

    // Hide any picker
    setPickerVisible(false);
    setShowFullPicker(false);
  }, [disabled, reactions]);

  /**
   * Toggle reaction picker visibility (functional updater to avoid stale state)
   */
  const togglePicker = useCallback(() => {
    if (disabled) return;
    setPickerVisible(prev => !prev);
  }, [disabled]);

  /**
   * Show expanded picker — also hide inline picker to avoid overlap
   */
  const showExpandedPicker = useCallback(() => {
    setPickerVisible(false);
    setShowFullPicker(true);
  }, []);

  /**
   * Long-press: open inline picker and mark the gesture
   */
  const onLongPressMain = useCallback(() => {
    if (disabled) return;
    longPressingRef.current = true;
    setPickerVisible(true);
  }, [disabled]);

  /**
   * Main press: swallow if it immediately follows long-press
   */
  const onPressMain = useCallback(() => {
    if (disabled) return;
    if (longPressingRef.current) {
      longPressingRef.current = false;
      return;
    }
    setPickerVisible(prev => !prev);
  }, [disabled]);

  /**
   * Clear the long-press swallow flag on gesture end
   */
  const onPressOutMain = useCallback(() => {
    longPressingRef.current = false;
  }, []);

  // Selected reaction color for label (matches halo)
  const currentReactionColor = useMemo(() => {
    if (!reactions.currentReaction) return undefined;
    const type = reactions.currentReaction as ReactionType;
    const emojiItem = byTitle[type];
    const fallbackConfig = DEFAULT_REACTIONS.find(r => r.type === type);
    const colorCandidate = emojiItem?.meta?.colorScheme || fallbackConfig?.color;
    const color = (typeof colorCandidate === 'string' && colorCandidate.trim().length > 0)
      ? colorCandidate
      : '#999999';
    return color;
  }, [reactions.currentReaction, byTitle]);

  /**
   * Render individual reaction button
   */
  const renderReactionButton = useCallback((title: string, size: 'small' | 'medium' = 'small') => {
    const type = title as ReactionType;
    const isSelected = reactions.currentReaction === type;
    const userCount = reactions.byReactionUserIds[type]?.length || 0;
    const emojiItem = byTitle[type];
    const fallbackConfig = DEFAULT_REACTIONS.find(r => r.type === type);
    const colorCandidate = emojiItem?.meta?.colorScheme || fallbackConfig?.color;
    const color = (typeof colorCandidate === 'string' && colorCandidate.trim().length > 0)
      ? colorCandidate
      : '#999999';

    return (
      <Button
        key={type}
        onPress={() => handleReactionPress(type)}
        disabled={disabled}
        unstyled
        mr="$1"
        px="$1"
        br="$3"
        bg={isSelected ? '$white' : 'transparent'}
        bw={isSelected ? 1 : 0}
        boc={color as any}
      >
        <XStack ai="center" gap="$1">
          {emojiItem?.url ? (
            <img
              src={emojiItem.url}
              style={{ width: size === 'small' ? 18 : 24, height: size === 'small' ? 18 : 24, opacity: disabled ? 0.5 : 1 }}
            />
          ) : (
            <CobuText fontSize={size === 'small' ? '$5' : '$6'} style={{ opacity: disabled ? 0.5 : 1 }}>
              {emojiItem?.text || fallbackConfig?.emoji}
            </CobuText>
          )}
          {userCount > 0 && (
            <CobuText
              variant="caption"
            >
              {userCount}
            </CobuText>
          )}
        </XStack>
      </Button>
    );
  }, [
    reactions.currentReaction,
    reactions.byReactionUserIds,
    handleReactionPress,
    disabled,
    byTitle
  ]);

  /**
   * Render reaction picker overlay (inline row)
   */
  const renderReactionPicker = useCallback(() => {
    if (!pickerVisible) return null;

    const inlinePickerWidth = reactions.currentReaction ? 230 : 210;

    return (
      <Stack pos="absolute" b={isParent ? '$12' : '$8'} l={0} r={0} zi={1000}>
        {/* backdrop to close on outside tap */}
        <Stack
          pos="absolute"
          t={-1000} b={-1000} l={-1000} r={-1000}
          onPress={() => setPickerVisible(false)}
        />
        <Card
          br="$6"
          shadowColor="#000"
          shadowOffset={{ width: 0, height: 2 }}
          shadowOpacity={0.25}
          shadowRadius={8}
          elevation={5}
          px="$2"
          py="$1"
          width={inlinePickerWidth}
          bg="white"
        >
          <XStack ai="center" gap="$1">
            {visibleTitles.map(title => renderReactionButton(title, 'small'))}
            {hiddenTitles.length > 0 && (
              <Button unstyled onPress={showExpandedPicker} p="$1" br="$3">
                <AddCircleOutlineIcon width={20} height={20} />
              </Button>
            )}
          </XStack>
        </Card>
      </Stack>
    );
  }, [pickerVisible, isParent, visibleTitles, hiddenTitles, renderReactionButton, showExpandedPicker]);

  /**
   * Render expanded reaction modal (palette grid)
   */
  const renderExpandedModal = useCallback(() => {
    return (
      <Dialog
        modal
        open={showFullPicker}
        onOpenChange={(open) => {
          setShowFullPicker(open);
          if (!open) setPickerVisible(false); // also close inline picker on dismiss
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay
            key="overlay"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            // animation={['medium', { opacity: { overshootClamping: true } }]}
            animation={undefined as any}
            backgroundColor="transparent"
          />
          <Dialog.Content
            backgroundColor="white"
            key="content"
            bordered
            elevate
            padding="$5"
            borderRadius="$4"
            margin="$5"
            maxWidth={320}
            // animation={['medium', { opacity: { overshootClamping: true } }]}
            animation={undefined as any}
            enterStyle={{ scale: 0.9, opacity: 0 }}
            exitStyle={{ scale: 0.95, opacity: 0 }}
            gap="$4"
          >
            <Card bg="$background" p="$5" br="$4">
              <CobuText variant="heading" fontSize="$4" fow="600" ta="center" mb="$4">
                {strings.reactions.addReaction}
              </CobuText>

              <YStack gap="$3">
                <XStack fw="wrap" gap="$2" jc="center">
                  {orderedEmojiTitles.map((title) => {
                    const type = title as ReactionType;
                    const isSelected = reactions.currentReaction === type;
                    const emojiItem = byTitle[type];
                    const fallbackConfig = DEFAULT_REACTIONS.find(r => r.type === type);
                    const borderColorCandidate = emojiItem?.meta?.colorScheme || fallbackConfig?.color;
                    const borderColor =
                      typeof borderColorCandidate === 'string' && borderColorCandidate.trim().length > 0
                        ? borderColorCandidate
                        : '#999999';

                    return (
                      <Button
                        key={type}
                        unstyled
                        onPress={() => handleReactionPress(type)}
                        ai="center"
                        p="$3"
                        br="$2"
                        //@ts-ignore
                        bc={isSelected ? (borderColor as any) + '20' : 'transparent'}
                        borderWidth={isSelected ? 1 : 0}
                        borderColor={borderColor as any}
                        minWidth={60}
                      >
                        <YStack ai="center" gap="$1">
                          {emojiItem?.url ? (
                            <img src={emojiItem.url} style={{ width: 28, height: 28 }} />
                          ) : (
                            <CobuText fontSize="$8">
                              {emojiItem?.text || fallbackConfig?.emoji}
                            </CobuText>
                          )}
                          <CobuText
                            variant="caption"
                            color="$colorSecondary"
                            fontSize="$1"
                            fow="500"
                            tt="capitalize"
                          >
                            {DEFAULT_EMOJI_TITLES.has(type.toLowerCase())
                              ? strings.reactions[type.toLowerCase() as keyof typeof strings.reactions]
                              : type}
                          </CobuText>
                        </YStack>
                      </Button>
                    );
                  })}
                </XStack>
              </YStack>
            </Card>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    );
  }, [
    showFullPicker,
    strings,
    handleReactionPress,
    reactions.currentReaction,
    orderedEmojiTitles,
    byTitle,
  ]);

  /**
   * Render current reaction display for comments
   */
  const renderCurrentReaction = useCallback(() => {
    if (!reactions.currentReaction) return null;

    const type = reactions.currentReaction as ReactionType;
    const fallbackConfig = reactions.getReactionConfig(type);
    const emojiItem = byTitle[type];

    const animation = reactions.showReactionAnimation
      ? (emojiItem?.animationType || fallbackConfig?.animationType || 'bounce')
      : undefined;

    return (
      <>
        {emojiItem?.url ? (
          <img src={emojiItem.url} style={{ width: 20, height: 20 }} />
        ) : (
          <CobuText fontSize="$5">{emojiItem?.text || fallbackConfig?.emoji}</CobuText>
        )}
      </>
    );
  }, [reactions, byTitle]);

  return (
    <YStack pos="relative">
      <XStack ai="center" gap="$3">
        {/* Main reaction button */}
        <Button
          onPress={onPressMain}
          onLongPress={onLongPressMain}
          onPressOut={onPressOutMain}
          disabled={disabled}
          unstyled
          px="$3"
          py="$2"
          br="$4"
          o={disabled ? 0.5 : 1}
        >
          <XStack ai="center" gap="$1">
            {reactions.currentReaction ? (
              renderCurrentReaction()
            ) : (
              <ReactIcon width={24} height={24} />
            )}

            <CobuText
              variant="caption"
              color="$colorSecondary"
              fontSize="$2"
              fow="500"
              style={reactions.currentReaction ? { color: currentReactionColor as any } : undefined}
            >
              {/* @ts-ignore */}
              {reactions.currentReaction
                ? (DEFAULT_EMOJI_TITLES.has(reactions.currentReaction.toLowerCase())
                    ? strings.reactions[reactions.currentReaction.toLowerCase() as keyof typeof strings.reactions]
                    : reactions.currentReaction)
                : strings.reactions.react}
            </CobuText>
          </XStack>
        </Button>
      </XStack>

      {/* Reaction picker overlay */}
      {renderReactionPicker()}

      {/* Expanded reaction modal */}
      {renderExpandedModal()}
    </YStack>
  );
});

EnhancedReactionSelector.displayName = 'EnhancedReactionSelector';

export default EnhancedReactionSelector;
